import React from "react";
import { ThemeProvider } from "@mui/material/styles";
import { init, ErrorBoundary, showReportDialog } from "@sentry/react";
import { BrowserRouter } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";

import { ChainbridgeRoutes } from "./routes";
import { ChainbridgeProvider, NetworkManagerProvider } from "./contexts";
import { AppWrapper } from "./layouts";
import { chainbridgeConfig } from "./chainbridgeConfig";
import { Web3Provider } from "@chainsafe/web3-context";
import { theme } from "./themes/GeneralTheme";
import { ScopedCssBaseline } from "@mui/material";
import injectedModule from '@web3-onboard/injected-wallets'
import walletConnectModule from '@web3-onboard/walletconnect'
import ledgerModule from '@web3-onboard/ledger'
import { WalletHelpers, WalletModule, WalletInit } from '@web3-onboard/common'
import { BurritoIcon, DENTNetIcon, DENTNetLogo } from './media/inline';

declare module "@mui/material/styles" {
  interface Theme {
    status: {
      danger: string;
    };
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    spacing: number;
    status?: {
      danger?: string;
    };
  }
}

if (
  process.env.NODE_ENV === "production" &&
  process.env.REACT_APP_SENTRY_DSN_URL &&
  process.env.REACT_APP_SENTRY_RELEASE
) {
  init({
    dsn: process.env.REACT_APP_SENTRY_DSN_URL,
    release: process.env.REACT_APP_SENTRY_RELEASE,
  });
}

const App: React.FC<NonNullable<unknown>> = () => {
  const {
    __RUNTIME_CONFIG__: {
      UI: { wrapTokenPage = false } = {},
      CHAINBRIDGE: { chains },
    },
  } = window;
  const tokens = chainbridgeConfig.chains
    .filter((c) => c.type === "Ethereum")
    .reduce((tca, bc: any) => {
      if (bc.networkId) {
        return {
          ...tca,
          [bc.networkId]: bc.tokens,
        };
      } else {
        return tca;
      }
    }, {});

  const networkIds = chainbridgeConfig.chains
    .filter((c) => c.type === "Ethereum" && c.networkId)
    .map((bc) => bc.networkId!) || [];

  const wcInitOptions = {
      projectId: chainbridgeConfig.walletConnectApiKey,
      requiredChains: [chainbridgeConfig.chains[0].networkId],
      optionalChains: [],
      dappUrl: 'http://main.dentnet.io/bridge/',
      qrModalOptions: {
        enableExplorer: true
      }
  }

  const ledger = ledgerModule({
    walletConnectVersion: 2,
    projectId: chainbridgeConfig.walletConnectApiKey,
    requiredChains: networkIds
  });

  function isWalletModule(object: any): object is WalletModule {
    return 'label' in object;
  }


  const walletConnect = walletConnectModule(wcInitOptions)
  const injected = injectedModule()
  const burrito: WalletInit = (helpers: WalletHelpers): WalletModule | null => {
    const wc = walletConnectModule({...wcInitOptions, qrModalOptions: { 
      explorerExcludedWalletIds: 'ALL',
      explorerRecommendedWalletIds: [
        // burrito wallet
        '8821748c25de9dbc4f72a691b25a6ddad9d7df12fa23333fd9c8b5fdc14cc819'
      ]
    }})(helpers);
    if (isWalletModule(wc)) {
      return {...wc, label: "Burrito", getIcon: () => new Promise((resolve) => resolve(BurritoIcon))};
    }
    return null;
  };

  return (
    <ThemeProvider theme={theme}>
      <ScopedCssBaseline enableColorScheme>
        <CssBaseline />
        <ErrorBoundary
          fallback={({ error, componentStack, eventId, resetError }) => (
            <div>
              <p>
                An error occurred and has been logged. If you would like to
                provide additional info to help us debug and resolve the issue,
                click the Provide Additional Details button
              </p>
              <p>{error?.message.toString()}</p>
              <p>{componentStack}</p>
              <p>{eventId}</p>
              <button
                onClick={() => showReportDialog({ eventId: eventId || "" })}
              >
                Provide Additional Details
              </button>
              <button onClick={resetError}>Reset error</button>
            </div>
          )}
          onReset={() => window.location.reload()}
        >
          {/* <ToasterProvider autoDismiss> */}
          <Web3Provider
            tokensToWatch={tokens}
            networkIds={networkIds}
            onboardConfig={{
              wallets: [injected, walletConnect, burrito, ledger],
              apiKey: '9b3a7d62-7111-45d5-9096-0639ddbc3347',
              chains: [
                {
                  id: `0x${chainbridgeConfig.chains[0].networkId?.toString(16)}`,
                  token: chainbridgeConfig.chains[0].nativeTokenSymbol,
                  label: chainbridgeConfig.chains[0].name,
                  rpcUrl: chainbridgeConfig.chains[0].rpcUrl,
                  namespace: 'evm'
                },
              ],
              appMetadata: {
                name: 'DENTNet Bridge',
                icon: DENTNetIcon,
                logo: DENTNetLogo,
                description: 'DENTNet Bridge transfers DENT tokens between Ethereum and DENTNet',
                recommendedInjectedWallets: [
                  { name: 'SubWallet', url: 'https://www.subwallet.app/' }
                ]
                // recommendedInjectedWallets: [
                //   { name: 'Coinbase', url: 'https://wallet.coinbase.com/' },
                //   { name: 'MetaMask', url: 'https://metamask.io' }
                // ]
              }
            }}
            checkNetwork={false}
            gasPricePollingInterval={0}
            gasPriceSetting="fast"
          >
            <NetworkManagerProvider>
              <ChainbridgeProvider chains={chains}>
                <BrowserRouter basename={process.env.PUBLIC_URL}>
                  <AppWrapper wrapTokenPage={wrapTokenPage}>
                    <ChainbridgeRoutes wrapTokenPage={wrapTokenPage} />
                  </AppWrapper>
                </BrowserRouter>
              </ChainbridgeProvider>
            </NetworkManagerProvider>
          </Web3Provider>
          {/* </ToasterProvider> */}
        </ErrorBoundary>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
};

export default App;
